import { FailedMessageWithHref } from 'common/ui';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { CalendarMeetingTemplateFormData } from 'modules/calendar/constants/calendarMeetingTemplateSchema';
import { useUserCalendar } from 'modules/calendar/hooks/useUserCalendar';
import { UserCalendarStatus } from 'generated-graphql/types';
import { useCreateCalendarMeetingTemplate } from 'modules/calendar/api/useCreateCalendarMeetingTemplate';
import { ToastType, useToast } from 'services/toast';
import { mapCreateCalendarMeetingTemplateSchema } from 'modules/calendar/utils/mapCreateCalendarMeetingTemplateSchema';
import { useLoadingOverlay } from 'services/loading/hooks';
import { UpsertCalendarMeetingTemplateForm } from 'modules/calendar/components/UpsertCalendarMeetingTemplateForm';
import { mapFormDataToMeetingTemplate } from 'modules/calendar/utils/mapFormDataToMeetingTemplate';

export const CalendarCreateMeetingTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();

  const { data: userCalendars } = useUserCalendar({
    status: UserCalendarStatus.Connected
  });

  const { mutateAsync: createCalendarMeetingTemplate } =
    useCreateCalendarMeetingTemplate();

  const handleSave = async (formData: CalendarMeetingTemplateFormData) => {
    try {
      showLoadingOverlay();
      const apiSchema = await mapCreateCalendarMeetingTemplateSchema(
        formData,
        userCalendars
      );

      const { data: createdData } = await createCalendarMeetingTemplate(
        apiSchema
      );

      showToast({
        title: 'Meeting template created',
        type: ToastType.Success
      });

      navigate(Paths.HOME_CALENDAR_MEETINGS, {
        state: {
          calendarMeetingTemplate: mapFormDataToMeetingTemplate({
            formData,
            userCalendars,
            id: createdData.id
          })
        }
      });
    } catch (error) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try creating this meeting template again."
            emailSubject="Failed to create meeting template"
          />
        ),
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  return (
    <UpsertCalendarMeetingTemplateForm
      type="create"
      breadcrumbs={[{ name: 'MEETINGS', path: Paths.HOME_CALENDAR_MEETINGS }]}
      currentBreadcrumb="Create Meeting Template"
      title="Create template"
      subtitle="Between one or more interviewers and one candidate."
      userCalendars={userCalendars}
      onSave={handleSave}
    />
  );
};
